import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LoginApi from '../../services/LoginApiClient';

const PASSWORD_MIN = 8;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token')?.trim() ?? '', [params]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null as string | null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('This link is missing a token. Use the full URL from your administrator.');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      setError(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setPending(true);
    try {
      await LoginApi.completePasswordReset(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-lg font-semibold text-emerald-800">
            ParachuteIQ
          </Link>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} aria-hidden />
            Sign in
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl bg-white border border-gray-200 shadow-sm p-8">
          <h1 className="text-xl font-semibold text-gray-900">Set your password</h1>
          {done ? (
            <>
              <p className="mt-2 text-sm text-gray-600">
                Your password was updated. You can sign in with your email and new password.
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-6 w-full px-4 py-2.5 rounded-lg btn-primary text-sm font-medium"
              >
                Go to sign in
              </button>
            </>
          ) : (
            <>
              <p className="mt-2 text-sm text-gray-600">
                Choose a new password for your account. This link expires after a limited time.
              </p>
              {error ? (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
                  {error}
                </div>
              ) : null}
              <form className="mt-6 space-y-4" onSubmit={(e) => void onSubmit(e)}>
                <div>
                  <label htmlFor="reset-pw" className="block text-sm font-medium text-gray-700 mb-1">
                    New password
                  </label>
                  <input
                    id="reset-pw"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={PASSWORD_MIN}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600"
                    autoFocus
                  />
                </div>
                <div>
                  <label htmlFor="reset-pw2" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm password
                  </label>
                  <input
                    id="reset-pw2"
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={PASSWORD_MIN}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600"
                  />
                </div>
                <button
                  type="submit"
                  disabled={pending}
                  className="w-full px-4 py-2.5 rounded-lg btn-primary text-sm font-medium disabled:opacity-50"
                >
                  {pending ? 'Saving…' : 'Save password'}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
